import React, { useEffect, useState, useCallback, useRef } from "react";
import { Wrench, Trash2, TrendingUp, Calendar, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const fmtMoney = (n) => "€" + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtMoneyDec = (n) => "€" + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function parseQuickInput(text) {
  // Returns {date, profit, job} from quick-log text.
  // Date can be ANYWHERE in the string (start/middle/end).
  // Profit = LARGEST standalone number ≥ €30 (engine sizes/years/stages skipped).
  if (!text) return null;
  let str = text.trim();
  const today = new Date();
  let date = today;

  // Search for keyword dates ANYWHERE in string
  const keywords = [
    { regex: /\byesterday\b/i, days: -1 },
    { regex: /\btoday\b/i, days: 0 },
    { regex: /χθες/i, days: -1 },
    { regex: /προχθ[εέ]ς/i, days: -2 },
    { regex: /σ[ηή]μερα/i, days: 0 },
  ];
  let dateFound = false;
  for (const k of keywords) {
    if (k.regex.test(str)) {
      date = new Date(today.getTime() + k.days * 86400000);
      str = str.replace(k.regex, " ");
      dateFound = true;
      break;
    }
  }

  // Search for DD/MM/YY (or DD/MM) date pattern ANYWHERE
  if (!dateFound) {
    const m = str.match(/(\d{1,2})[\/\-\.](\d{1,2})(?:[\/\-\.](\d{2,4}))?/);
    if (m) {
      const day = parseInt(m[1]);
      const month = parseInt(m[2]) - 1;
      const year = m[3] ? (parseInt(m[3]) > 99 ? parseInt(m[3]) : 2000 + parseInt(m[3])) : today.getFullYear();
      if (day >= 1 && day <= 31 && month >= 0 && month <= 11) {
        date = new Date(year, month, day);
        str = str.replace(m[0], " ");
      }
    }
  }

  // Cleanup: arrows, multiple spaces
  str = str.replace(/->/g, " ").replace(/[:>]+/g, " ").replace(/\s+/g, " ").trim();

  // Find numbers — must NOT be inside word like "mx5" (preceded by letter)
  // BUT can be followed by currency unit (450euro, 450€, 450eu)
  // "mx5" → "5" preceded by "x" → skip
  // "stage 1" → "1" standalone → match
  // "450euro" → "450" preceded by space → match (capture just digits)
  const numberRegex = /(?<![A-Za-z])(\d+(?:[.,]\d+)?)(?:\s*(?:euro|eu|€))?/g;
  const matches = [...str.matchAll(numberRegex)];
  if (matches.length === 0) return null;

  // Pick largest number ≥ 30 as profit (engine sizes 1.8/2.0/etc. and stage 1/2 ignored)
  let bestMatch = null;
  let bestVal = -1;
  for (const m of matches) {
    const v = parseFloat(m[1].replace(",", "."));
    if (v >= 30 && v > bestVal) {
      bestVal = v;
      bestMatch = m;
    }
  }
  if (!bestMatch) return null;  // no valid price found
  const profit = bestVal;

  // Job = string with the chosen number removed
  let job = str.substring(0, bestMatch.index) + str.substring(bestMatch.index + bestMatch[0].length);
  job = job.replace(/[\->:]+/g, " ").replace(/\s+/g, " ").trim();
  if (!job) job = "Sale";
  job = job.charAt(0).toUpperCase() + job.slice(1);

  return { date, profit, job };
}

function StatCard({ label, total, count, color }) {
  return (
    <div style={{ background: "#13161f", border: `1px solid ${color}55`, borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: total > 0 ? color : "#aaa", marginTop: 4 }}>
        {fmtMoney(total)}
      </div>
      <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{count || 0} sales</div>
    </div>
  );
}

const MONTH_NAMES_GR = ["Ιαν","Φεβ","Μαρ","Απρ","Μάι","Ιουν","Ιουλ","Αυγ","Σεπ","Οκτ","Νοε","Δεκ"];

function MonthlyChart({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.total));
  return (
    <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, color: "#fff", marginBottom: 14, fontWeight: 700 }}>📊 Profit ανά μήνα</div>
      <div style={{ display: "flex", alignItems: "end", gap: 8, height: 140, paddingBottom: 24 }}>
        {data.slice(-12).map((d, i) => {
          const h = max > 0 ? (d.total / max * 100) : 0;
          const monthName = MONTH_NAMES_GR[(d.month_num || parseInt(d.month?.split("-")[1])) - 1];
          const yearShort = (d.year || parseInt(d.month?.split("-")[0]))?.toString().slice(-2);
          return (
            <div key={i} style={{ flex: 1, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%", position: "relative" }}>
              <div style={{ fontSize: 10, color: "#22c55e", fontWeight: 700, marginBottom: 4 }}>{fmtMoney(d.total)}</div>
              <div style={{ fontSize: 9, color: "#666" }}>{d.count} sales</div>
              <div style={{ height: `${h}%`, background: "linear-gradient(180deg, #22c55e, #15803d)", borderRadius: 4, minHeight: 6, marginTop: 4 }}/>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 6, fontWeight: 600 }}>{monthName} '{yearShort}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AnalysisCard({ analysis }) {
  if (!analysis || !analysis.best_month) return null;
  const trend = analysis.trend_pct;
  return (
    <div style={{ background: "#13161f", border: "1px solid #a855f755", borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, color: "#a855f7", fontWeight: 700, marginBottom: 12 }}>🧠 Ανάλυση</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, fontSize: 12 }}>
        <div style={{ padding: 10, background: "#0a0c14", borderRadius: 8, borderLeft: "3px solid #22c55e" }}>
          <div style={{ fontSize: 10, color: "#888" }}>🏆 ΚΑΛΥΤΕΡΟΣ ΜΗΝΑΣ</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#22c55e" }}>{fmtMoney(analysis.best_month.total)}</div>
          <div style={{ fontSize: 11, color: "#aaa" }}>
            {MONTH_NAMES_GR[analysis.best_month.month_num - 1]} {analysis.best_month.year} · {analysis.best_month.count} sales
          </div>
          {analysis.best_month.best_amount > 0 && (
            <div style={{ fontSize: 10, color: "#666", marginTop: 4 }}>
              Best sale: {fmtMoney(analysis.best_month.best_amount)} "{analysis.best_month.best_job?.substring(0, 25)}"
            </div>
          )}
        </div>

        {analysis.worst_month && analysis.worst_month.month !== analysis.best_month.month && (
          <div style={{ padding: 10, background: "#0a0c14", borderRadius: 8, borderLeft: "3px solid #ef4444" }}>
            <div style={{ fontSize: 10, color: "#888" }}>📉 ΧΕΙΡΟΤΕΡΟΣ</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#ef4444" }}>{fmtMoney(analysis.worst_month.total)}</div>
            <div style={{ fontSize: 11, color: "#aaa" }}>
              {MONTH_NAMES_GR[analysis.worst_month.month_num - 1]} {analysis.worst_month.year} · {analysis.worst_month.count} sales
            </div>
          </div>
        )}

        <div style={{ padding: 10, background: "#0a0c14", borderRadius: 8, borderLeft: "3px solid #3b82f6" }}>
          <div style={{ fontSize: 10, color: "#888" }}>📊 ΜΕΣΟΣ ΟΡΟΣ ΜΗΝΑ</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#3b82f6" }}>{fmtMoney(analysis.avg_monthly)}</div>
          <div style={{ fontSize: 11, color: "#aaa" }}>
            {analysis.total_months_active} μήνες ενεργοί · ~{analysis.avg_sales_per_month} sales/μήνα
          </div>
        </div>

        {trend !== undefined && (
          <div style={{ padding: 10, background: "#0a0c14", borderRadius: 8, borderLeft: `3px solid ${trend >= 0 ? "#22c55e" : "#ef4444"}` }}>
            <div style={{ fontSize: 10, color: "#888" }}>📈 ΤΑΣΗ (3μ vs 3μ)</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: trend >= 0 ? "#22c55e" : "#ef4444" }}>
              {trend >= 0 ? "+" : ""}{trend.toFixed(1)}%
            </div>
            <div style={{ fontSize: 11, color: "#aaa" }}>
              {fmtMoney(analysis.recent_3m_avg)} vs {fmtMoney(analysis.prior_3m_avg)}
            </div>
          </div>
        )}

        {analysis.year_totals && analysis.year_totals.length > 0 && analysis.year_totals.map(y => (
          <div key={y.year} style={{ padding: 10, background: "#0a0c14", borderRadius: 8, borderLeft: "3px solid #f59e0b" }}>
            <div style={{ fontSize: 10, color: "#888" }}>📅 ΕΤΟΣ {y.year}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b" }}>{fmtMoney(y.total)}</div>
            <div style={{ fontSize: 11, color: "#aaa" }}>{y.count} sales total</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthlyTable({ data }) {
  if (!data || data.length === 0) return null;
  const sorted = [...data].sort((a, b) => b.month.localeCompare(a.month));
  return (
    <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
      <div style={{ padding: "12px 16px", fontSize: 13, fontWeight: 700, color: "#fff", borderBottom: "1px solid #2a2d39" }}>
        📅 Breakdown ανά μήνα
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#0a0c14", color: "#666", textTransform: "uppercase", fontSize: 10 }}>
            <th style={{ padding: 10, textAlign: "left" }}>Μήνας</th>
            <th style={{ padding: 10, textAlign: "right" }}>Total</th>
            <th style={{ padding: 10, textAlign: "right" }}>Sales</th>
            <th style={{ padding: 10, textAlign: "right" }}>Avg/sale</th>
            <th style={{ padding: 10, textAlign: "right" }}>Best sale</th>
            <th style={{ padding: 10, textAlign: "left" }}>Best job</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((m, i) => {
            const monthName = MONTH_NAMES_GR[m.month_num - 1];
            return (
              <tr key={m.month} style={{ borderTop: "1px solid #1a1d29" }}>
                <td style={{ padding: 10, color: "#fff", fontWeight: 600 }}>{monthName} {m.year}</td>
                <td style={{ padding: 10, color: "#22c55e", textAlign: "right", fontWeight: 700, fontSize: 14 }}>{fmtMoney(m.total)}</td>
                <td style={{ padding: 10, color: "#aaa", textAlign: "right" }}>{m.count}</td>
                <td style={{ padding: 10, color: "#888", textAlign: "right" }}>{fmtMoney(m.avg_per_sale)}</td>
                <td style={{ padding: 10, color: "#a855f7", textAlign: "right", fontWeight: 600 }}>{fmtMoney(m.best_amount)}</td>
                <td style={{ padding: 10, color: "#666", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  "{m.best_job}"
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function MagaziTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quickInput, setQuickInput] = useState("");
  const [parsed, setParsed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const inputRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!token) { setError("Not logged in"); setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/skynet-brain/magazi-sales`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401) { setError("Session expired"); setLoading(false); return; }
      const j = await r.json();
      if (j.error) setError(j.error);
      else { setData(j); setError(null); }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  // Live preview as user types
  useEffect(() => {
    setParsed(parseQuickInput(quickInput));
  }, [quickInput]);

  const submit = async () => {
    console.log("[Magazi] submit called", { quickInput, parsed });
    if (!quickInput.trim()) {
      setMsg("⚠️ Άδειο πεδίο");
      return;
    }
    if (!parsed) {
      setMsg("⚠️ Πρέπει να έχει ποσό ≥ €30. π.χ. \"400 swap mx5\"");
      return;
    }
    setSaving(true);
    setMsg("⏳ Saving...");
    const savedProfit = parsed.profit;
    const savedJob = parsed.job;
    const savedDate = parsed.date;
    try {
      console.log("[Magazi] POST request");
      const r = await fetch(`${API}/api/skynet-brain/magazi-sales`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          profit: savedProfit,
          job: savedJob,
          date: savedDate.toISOString(),
        })
      });
      console.log("[Magazi] POST status:", r.status);
      if (r.status === 401) {
        setMsg("❌ Session expired — logout/login");
        setSaving(false);
        return;
      }
      const j = await r.json();
      console.log("[Magazi] POST response:", j);
      if (j.ok) {
        setMsg(`✅ Added: +${fmtMoney(savedProfit)} (${savedJob}) on ${savedDate.toLocaleDateString("el-GR")}`);
        setQuickInput("");
        setParsed(null);
        // Don't await — fire & forget for UI snappiness
        fetchData();
        setTimeout(() => setMsg(""), 3000);
        setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 100);
      } else {
        setMsg("❌ " + (j.error || j.detail || JSON.stringify(j)));
      }
    } catch (e) {
      console.error("[Magazi] submit error:", e);
      setMsg("❌ Network error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const [confirmDel, setConfirmDel] = useState(null);
  const deleteSale = async (id) => {
    if (!id) { console.error("No id to delete"); return; }
    try {
      const r = await fetch(`${API}/api/skynet-brain/magazi-sales/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (j.ok && j.deleted > 0) {
        setConfirmDel(null);
        fetchData();
      } else {
        alert("Delete failed: " + (j.error || "unknown"));
      }
    } catch (e) { alert("Error: " + e.message); }
  };

  if (error && !data) return <div style={{ padding: 40, color: "#ef4444", textAlign: "center" }}>{error}</div>;
  if (loading && !data) return <div style={{ padding: 40, color: "#888", textAlign: "center" }}>Loading…</div>;
  if (!data) return null;

  const stats = data.stats || {};

  return (
    <div style={{ padding: "16px 20px", color: "#eee", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Wrench size={22} style={{ color: "#f59e0b" }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>Magazi</div>
          <span style={{ fontSize: 10, padding: "3px 8px", background: "#f59e0b33", color: "#f59e0b", borderRadius: 4, fontWeight: 600 }}>
            {data.n_sales || 0} sales · avg €{stats.avg_per_sale || 0}
          </span>
        </div>
        <button onClick={fetchData} style={{ background: "transparent", border: "1px solid #2a2d39", color: "#aaa", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {/* Quick-log input */}
      <div style={{ background: "linear-gradient(135deg, #1e1f2b, #15171f)", border: "2px solid #f59e0b88", borderRadius: 14, padding: 16, marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>⚡ QUICK LOG</div>
        <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
          <input
            ref={inputRef}
            type="text"
            value={quickInput}
            onChange={e => setQuickInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submit(); }}
            placeholder='π.χ. "400 swap mx5"  ή  "yesterday 600 program"  ή  "8/5 350 cam swap"'
            style={{
              flex: 1, background: "#0a0c14", border: "1px solid #2a2d39", color: "#fff",
              borderRadius: 8, padding: "12px 14px", fontSize: 14, outline: "none",
            }}
            disabled={saving}
            autoFocus
          />
          <button onClick={submit} disabled={saving || !parsed}
            style={{
              padding: "0 24px", background: parsed ? "#f59e0b" : "#374151",
              color: "#000", border: "none", borderRadius: 8, fontWeight: 700, cursor: parsed ? "pointer" : "not-allowed",
            }}>
            {saving ? "..." : "+ ADD"}
          </button>
        </div>
        {/* Live preview */}
        {parsed && (
          <div style={{ marginTop: 10, padding: 10, background: "#0a0c14", borderRadius: 6, fontSize: 12, display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#888" }}>📅 {parsed.date.toLocaleDateString("el-GR")}</span>
            <span style={{ color: "#22c55e", fontWeight: 700 }}>{fmtMoney(parsed.profit)}</span>
            <span style={{ color: "#aaa", fontStyle: "italic" }}>"{parsed.job}"</span>
          </div>
        )}
        {msg && <div style={{ marginTop: 8, fontSize: 12, color: msg.startsWith("✅") ? "#22c55e" : "#ef4444" }}>{msg}</div>}
        <div style={{ marginTop: 10, fontSize: 10, color: "#666" }}>
          Tips: γράψε αριθμό + περιγραφή. Πρόσθεσε "yesterday", "χθες", "8/5" για άλλη ημερομηνία. Enter για instant save.
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
        <StatCard label="Today" total={stats.today?.total} count={stats.today?.count} color="#22c55e" />
        <StatCard label="This Week" total={stats.this_week?.total} count={stats.this_week?.count} color="#3b82f6" />
        <StatCard label="This Month" total={stats.this_month?.total} count={stats.this_month?.count} color="#a855f7" />
        <StatCard label="This Year" total={stats.this_year?.total} count={stats.this_year?.count} color="#f59e0b" />
        <StatCard label="All-time" total={stats.all_time?.total} count={stats.all_time?.count} color="#ec4899" />
      </div>

      {/* Analysis */}
      <AnalysisCard analysis={data.analysis} />

      {/* Monthly chart */}
      <MonthlyChart data={data.monthly_series} />

      {/* Monthly breakdown table */}
      <MonthlyTable data={data.monthly_series} />

      {/* Sales list */}
      <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", fontSize: 12, fontWeight: 600, color: "#fff", borderBottom: "1px solid #2a2d39" }}>
          📋 Sales History ({data.n_sales || 0})
        </div>
        {(!data.sales || data.sales.length === 0) ? (
          <div style={{ padding: 40, textAlign: "center", color: "#666" }}>
            Δεν έχεις καμία πώληση ακόμα. Γράψε στο πεδίο πάνω π.χ. <b style={{ color: "#f59e0b" }}>"400 swap mx5"</b> και πάτα Enter.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#0a0c14", color: "#666", textTransform: "uppercase", fontSize: 10 }}>
                <th style={{ padding: 10, textAlign: "left" }}>Date</th>
                <th style={{ padding: 10, textAlign: "left" }}>Job</th>
                <th style={{ padding: 10, textAlign: "right" }}>Profit</th>
                <th style={{ padding: 10, width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.sales.map((s, i) => (
                <tr key={s._id || i} style={{ borderTop: "1px solid #1a1d29" }}>
                  <td style={{ padding: 10, color: "#aaa" }}>
                    {new Date(s.date).toLocaleDateString("el-GR")}
                  </td>
                  <td style={{ padding: 10, color: "#fff" }}>{s.job}</td>
                  <td style={{ padding: 10, color: "#22c55e", textAlign: "right", fontWeight: 700 }}>
                    {fmtMoney(s.profit)}
                  </td>
                  <td style={{ padding: 10, textAlign: "center" }}>
                    {confirmDel === s._id ? (
                      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                        <button onClick={() => deleteSale(s._id)}
                          style={{ padding: "2px 8px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 600 }}>
                          DEL
                        </button>
                        <button onClick={() => setConfirmDel(null)}
                          style={{ padding: "2px 8px", background: "#374151", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10 }}>
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDel(s._id)}
                        style={{ background: "transparent", border: "none", color: "#666", cursor: "pointer" }}
                        title="Delete">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
